/**
 * Per-port IP allowlist: the canonical form of an entry.
 *
 * The list is part of the signed manifest, so its spelling is part of the
 * manifest hash. This module reproduces `NormalizeAllowFrom` in the go fork
 * (`github.com/DDWS-com/go` manifest/v2beta3/allowfrom.go) exactly, and
 * `allowFrom.test.ts` checks it against that package's golden vectors
 * (`protocol/allow-from-vectors.json`). The Go package is the authority: when
 * the two disagree, this file is wrong.
 *
 * Canonical: a bare address becomes /32 or /128, host bits are cleared, IPv6
 * is lowercase and compressed (RFC 5952, as Go's netip prints it), the list is
 * sorted bytewise. Refused: duplicates, everyone (/0), non-public ranges, a
 * range that contains one, IPv4-mapped IPv6, and more than 20 entries.
 */

export const MAX_ALLOW_FROM_ENTRIES = 20

/** The capability a provider advertises when it enforces allowlists (D2). */
export const ALLOWLIST_CAPABILITY = 'capabilities/network/allowlist'

export type AllowFromRule = 'empty' | 'invalid' | 'ipv4-mapped' | 'everyone' | 'non-public' | 'contains-non-public' | 'duplicate' | 'too-many'

export type CanonicalEntry =
  | { ok: true; value: string; /** set when the input was rewritten in a way the tenant should see */ note?: string }
  | { ok: false; rule: AllowFromRule; message: string }

interface Prefix {
  /** 4 bytes for IPv4, 16 for IPv6 */
  bytes: number[]
  bits: number
}

const NON_PUBLIC: readonly string[] = [
  '0.0.0.0/8', '10.0.0.0/8', '100.64.0.0/10', '127.0.0.0/8', '169.254.0.0/16', '172.16.0.0/12',
  '192.0.0.0/24', '192.168.0.0/16', '198.18.0.0/15', '224.0.0.0/4', '240.0.0.0/4',
  '::/128', '::1/128', '::ffff:0:0/96', '100::/64', 'fc00::/7', 'fe80::/10', 'fec0::/10', 'ff00::/8',
]

function parseIPv4(s: string): number[] | null {
  const parts = s.split('.')
  if (parts.length !== 4) return null
  const out: number[] = []
  for (const part of parts) {
    // Go's netip refuses leading zeros, which some parsers read as octal.
    if (!/^\d{1,3}$/.test(part) || (part.length > 1 && part.startsWith('0'))) return null
    const n = Number(part)
    if (n > 255) return null
    out.push(n)
  }
  return out
}

function parseIPv6(s: string): number[] | null {
  if (s.includes('%')) return null
  let head = s
  let tail = ''
  const dbl = s.indexOf('::')
  if (dbl !== -1) {
    if (s.indexOf('::', dbl + 1) !== -1) return null
    head = s.slice(0, dbl)
    tail = s.slice(dbl + 2)
  }
  const groups = (part: string): number[] | null => {
    if (part === '') return []
    const out: number[] = []
    const fields = part.split(':')
    for (const [i, field] of fields.entries()) {
      if (i === fields.length - 1 && field.includes('.')) {
        const v4 = parseIPv4(field)
        if (!v4) return null
        out.push(((v4[0] ?? 0) << 8) | (v4[1] ?? 0), ((v4[2] ?? 0) << 8) | (v4[3] ?? 0))
        continue
      }
      if (!/^[0-9a-fA-F]{1,4}$/.test(field)) return null
      out.push(parseInt(field, 16))
    }
    return out
  }
  const h = groups(head)
  const t = groups(tail)
  if (!h || !t) return null
  // An embedded IPv4 is only allowed at the very end.
  if (dbl !== -1 && head.includes('.')) return null
  let all: number[]
  if (dbl === -1) {
    if (h.length !== 8) return null
    all = h
  } else {
    const fill = 8 - h.length - t.length
    if (fill < 1) return null
    all = [...h, ...new Array<number>(fill).fill(0), ...t]
  }
  return all.flatMap((g) => [g >> 8, g & 0xff])
}

function parseAddr(s: string): number[] | null {
  return s.includes(':') ? parseIPv6(s) : parseIPv4(s)
}

function mask(bytes: number[], bits: number): number[] {
  return bytes.map((b, i) => {
    const keep = Math.max(0, Math.min(8, bits - i * 8))
    return keep === 8 ? b : b & ((0xff << (8 - keep)) & 0xff)
  })
}

function formatIPv6(bytes: number[]): string {
  const g: number[] = []
  for (let i = 0; i < 16; i += 2) g.push(((bytes[i] ?? 0) << 8) | (bytes[i + 1] ?? 0))
  // The longest run of two or more zero groups, leftmost on a tie (netip).
  let start = -1
  let len = 0
  for (let i = 0; i < 8;) {
    if (g[i] !== 0) { i += 1; continue }
    let j = i
    while (j < 8 && g[j] === 0) j += 1
    if (j - i >= 2 && j - i > len) { start = i; len = j - i }
    i = j
  }
  const hex = (xs: number[]) => xs.map((x) => x.toString(16)).join(':')
  if (start === -1) return hex(g)
  return `${hex(g.slice(0, start))}::${hex(g.slice(start + len))}`
}

function formatPrefix(p: Prefix): string {
  const addr = p.bytes.length === 4 ? p.bytes.join('.') : formatIPv6(p.bytes)
  return `${addr}/${p.bits}`
}

function parsePrefix(s: string): Prefix | null {
  const slash = s.indexOf('/')
  const addrPart = slash === -1 ? s : s.slice(0, slash)
  const bytes = parseAddr(addrPart)
  if (!bytes) return null
  if (slash === -1) return { bytes, bits: bytes.length * 8 }
  const bitsPart = s.slice(slash + 1)
  if (!/^\d+$/.test(bitsPart) || (bitsPart.length > 1 && bitsPart.startsWith('0'))) return null
  const bits = Number(bitsPart)
  if (bits > bytes.length * 8) return null
  return { bytes, bits }
}

const nonPublic: Prefix[] = NON_PUBLIC.map((s) => {
  const p = parsePrefix(s)
  if (!p) throw new Error(`bad non-public prefix ${s}`)
  return p
})

function contains(outer: Prefix, inner: Prefix): boolean {
  if (outer.bytes.length !== inner.bytes.length || outer.bits > inner.bits) return false
  const a = mask(outer.bytes, outer.bits)
  const b = mask(inner.bytes, outer.bits)
  return a.every((x, i) => x === b[i])
}

function isIPv4Mapped(bytes: number[]): boolean {
  return bytes.length === 16 && bytes.slice(0, 10).every((b) => b === 0) && bytes[10] === 0xff && bytes[11] === 0xff
}

/** Canonicalise one entry, or say which rule refuses it and why. */
export function canonicalAllowFromEntry(entry: string): CanonicalEntry {
  const s = entry.trim()
  if (s === '') return { ok: false, rule: 'empty', message: 'Enter an IP address or a range.' }

  const parsed = parsePrefix(s)
  if (!parsed) {
    return { ok: false, rule: 'invalid', message: 'Enter an IP address like 203.0.113.7 or a range like 198.51.100.0/24.' }
  }
  if (isIPv4Mapped(parsed.bytes)) return { ok: false, rule: 'ipv4-mapped', message: 'Write this as an IPv4 address, like 203.0.113.7.' }

  const prefix: Prefix = { bytes: mask(parsed.bytes, parsed.bits), bits: parsed.bits }
  if (prefix.bits === 0) return { ok: false, rule: 'everyone', message: 'That’s everyone. Choose Anyone instead.' }

  for (const np of nonPublic) {
    if (np.bytes.length !== prefix.bytes.length) continue
    if (contains(np, prefix)) return { ok: false, rule: 'non-public', message: 'Traffic from the internet never comes from this range.' }
    if (contains(prefix, np)) {
      return { ok: false, rule: 'contains-non-public', message: `This range includes ${formatPrefix(np)}, which internet traffic never comes from. Split it into public ranges.` }
    }
  }

  const value = formatPrefix(prefix)
  const hostBitsCleared = parsed.bytes.some((b, i) => b !== prefix.bytes[i])
  return hostBitsCleared
    ? { ok: true, value, note: `Saved as ${value}: a range starts at its first address.` }
    : { ok: true, value }
}

export type NormalizedList = { ok: true; value: string[] } | { ok: false; rule: AllowFromRule; message: string; index?: number }

/** Canonicalise, de-duplicate-check and sort a whole list, as the manifest needs it. */
export function normalizeAllowFrom(entries: readonly string[]): NormalizedList {
  if (entries.length > MAX_ALLOW_FROM_ENTRIES) {
    return { ok: false, rule: 'too-many', message: `A port can have at most ${MAX_ALLOW_FROM_ENTRIES} addresses.` }
  }
  const seen = new Set<string>()
  const out: string[] = []
  for (const [index, entry] of entries.entries()) {
    const c = canonicalAllowFromEntry(entry)
    if (!c.ok) return { ...c, index }
    if (seen.has(c.value)) return { ok: false, rule: 'duplicate', message: `${c.value} is already in the list.`, index }
    seen.add(c.value)
    out.push(c.value)
  }
  // Bytewise, as Go's sort.Strings: every character here is ASCII, where
  // UTF-16 code-unit order and byte order agree.
  out.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
  return { ok: true, value: out }
}

/** The family of a canonical entry, for display. */
export function allowFromFamily(entry: string): 'IPv4' | 'IPv6' {
  return entry.includes(':') ? 'IPv6' : 'IPv4'
}

/**
 * Whether a provider advertises allowlist enforcement. Accepts both shapes the
 * console sees: the explorer's `{key: value}` map and the chain record's
 * `[{key, value}]` list (GET /api/tenant/providers/<address> relays the latter).
 */
export function providerEnforcesAllowlist(attributes: unknown): boolean {
  if (Array.isArray(attributes)) {
    return attributes.some((a) => typeof a === 'object' && a !== null && (a as { key?: unknown }).key === ALLOWLIST_CAPABILITY && (a as { value?: unknown }).value === 'true')
  }
  if (typeof attributes === 'object' && attributes !== null) return (attributes as Record<string, unknown>)[ALLOWLIST_CAPABILITY] === 'true'
  return false
}

export interface PortAccessRow {
  service: string
  port: number
  proto: 'TCP' | 'UDP'
  /** Canonical list, or null for Anyone. */
  allowFrom: string[] | null
}

/**
 * Each global port's access, read from the SDL an instance was signed with.
 * Unreadable SDL or entries answer an empty list rather than guessing: the
 * instance page then shows nothing instead of a wrong "Anyone".
 */
export function portAccessFromSdl(doc: unknown): PortAccessRow[] {
  const services = typeof doc === 'object' && doc !== null ? (doc as { services?: unknown }).services : undefined
  if (typeof services !== 'object' || services === null) return []
  const rows: PortAccessRow[] = []
  for (const [service, raw] of Object.entries(services as Record<string, unknown>)) {
    const exposes = typeof raw === 'object' && raw !== null ? (raw as { expose?: unknown }).expose : undefined
    for (const e of Array.isArray(exposes) ? exposes : []) {
      if (typeof e !== 'object' || e === null) continue
      const expose = e as { port?: unknown; proto?: unknown; to?: unknown }
      for (const t of Array.isArray(expose.to) ? expose.to : []) {
        if (typeof t !== 'object' || t === null || (t as { global?: unknown }).global !== true) continue
        const list = (t as { allow_from?: unknown }).allow_from
        let allowFrom: string[] | null = null
        if (Array.isArray(list) && list.length > 0) {
          const n = normalizeAllowFrom(list.map(String))
          if (!n.ok) return []
          allowFrom = n.value
        }
        rows.push({ service, port: Number(expose.port), proto: String(expose.proto ?? 'tcp').toLowerCase() === 'udp' ? 'UDP' : 'TCP', allowFrom })
      }
    }
  }
  return rows
}
